import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { ApplicationAccess } from '@/api/types'

/**
 * Who may open a database or dashboard.
 *
 * Readable only by the people who may change it -- the application's creator
 * and the workspace admins -- so nothing subscribes to it speculatively. What
 * everybody else needs is already on the application itself, as
 * `user_can_access`, because a closed application is still listed.
 */
export function applicationAccessQuery(applicationId: number) {
  return queryOptions({
    queryKey: qk.applicationAccess(applicationId),
    queryFn: (): Promise<ApplicationAccess> =>
      unwrap(
        getApi().GET('/api/applications/{application_id}/access/', {
          params: { path: { application_id: applicationId } },
        }),
      ),
  })
}

export function useApplicationAccess(applicationId: number) {
  return useQuery(applicationAccessQuery(applicationId))
}

/**
 * Replaces the whole access configuration.
 *
 * A `PUT` of the complete allowlist, for the same reason the table endpoint is
 * one: "these people" is a statement about all of them, and adding one at a
 * time would let two tabs remove somebody by not mentioning them.
 */
export function setApplicationAccessMutation(
  queryClient: QueryClient,
  workspaceId: number,
  applicationId: number,
) {
  return mutationOptions({
    mutationFn: (access: ApplicationAccess): Promise<ApplicationAccess> =>
      unwrap(
        getApi().PUT('/api/applications/{application_id}/access/', {
          params: { path: { application_id: applicationId } },
          body: access,
        }),
      ),
    onSuccess: async (access) => {
      queryClient.setQueryData(qk.applicationAccess(applicationId), access)
      // Every application row carries `user_can_access`, and a hidden one stops
      // being listed at all -- including, possibly, for the person who just
      // hid it. The list has to come back from the server rather than be patched.
      await queryClient.invalidateQueries({ queryKey: qk.applications(workspaceId) })
    },
  })
}

export function useSetApplicationAccess(workspaceId: number, applicationId: number) {
  return useMutation(
    setApplicationAccessMutation(useQueryClient(), workspaceId, applicationId),
  )
}
